<template>
    <div id="personal-information-form">
        <v-card>
            <announcement :announcement="announcement"></announcement>
            <v-card-title>
                <v-icon left :color="kingsGold">mdi-account</v-icon>Personal Information Form
            </v-card-title>
            <v-alert v-if="alert" :color="dangerColor" class="mr-3 ml-3 mb-0" dense icon="mdi-alert-circle" text
                prominent>
                There are one or more required form fields that are empty. Please verify that all required form fields
                are filled in.
            </v-alert>
            <v-card-text>
                <v-form ref="personalInformationForm" v-model="valid" lazy-validation>
                    <v-container class="px-0" fluid>
                        <v-card-subtitle class="px-0">
                            <v-icon :color="kingsGold" left>mdi-account-box</v-icon>Basic Information
                        </v-card-subtitle>
                        <v-row>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="applicant.firstName" label="First Name" :rules="firstNameRules"
                                    required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="applicant.lastName" label="Last Name" :rules="lastNameRules"
                                    required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="applicant.preferredName" label="Preferred Name (Optional)">
                                </v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-menu ref="menu" v-model="dateOfBirthMenu" :close-on-content-click="false"
                                    transition="scale-transition" offset-y min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="applicant.dateOfBirth" v-bind="attrs" v-on="on"
                                            label="Date of Birth" prepend-icon="mdi-calendar-account" readonly required
                                            :rules="dateOfBirthRules">
                                        </v-text-field>
                                    </template>
                                    <v-date-picker ref="picker" v-model="applicant.dateOfBirth"
                                        :max="new Date().toISOString().substr(0, 10)" min="1950-01-01"
                                        @change="saveDateOfBirth"></v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-select v-model="applicant.gender" :items="genderItems" label="Gender"
                                    placeholder="Please select..." required :rules="genderRules">
                                </v-select>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-radio-group 
                                    v-model="applicant.prefpronoun"
                                    dense
                                    row
                                    @change="enterPronounManually"
                                >
                                    <v-radio
                                        label="He/Him"
                                        value="He/Him"
                                    ></v-radio>
                                    <v-radio
                                        label="She/Her"
                                        value="She/Her"
                                    ></v-radio>
                                    <v-radio
                                        label="Manual Entry"
                                        value="Enter here"
                                    >
                                    </v-radio>
                                </v-radio-group>
                                <v-text-field
                                    v-if="pronounManualEnter"
                                    v-model="applicant.prefpronoun"
                                    outlined
                                    dense
                                    clearable
                                    label="Enter your preferred pronoun here"
                                    :rules="pronounRules"
                                ></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-tooltip top color="#004236">
                                    <template v-slot:activator="{ on, attrs }">
                                        <span
                                            v-bind="attrs"
                                            v-on="on"
                                        >
                                            <v-badge
                                                icon="mdi-information-variant"
                                                color="#004236"
                                            >
                                                <v-checkbox
                                                    v-model="applicant.sixftorover"
                                                    label="I'm over 6' tall"
                                                    true-value="yes"
                                                    false-value="no"
                                                    @change="showHeightOptions"
                                                ></v-checkbox>
                                            </v-badge>
                                        </span>
                                    </template>
                                    <span>Some residence bed placements are not suitable for those over 6' tall.</span>
                                </v-tooltip>
                            </v-col>
                            <v-col cols="12" md="6" v-if="showheightoptions">
                                <v-select v-model="applicant.sixftoroverdetail" dense hint="If you are taller than 6' 5” please contact Residence Office directly." :items="sixftoroverdetailitems" label="Please specify your height: " persistent-hint outlined></v-select>
                            </v-col>
                        </v-row>
                        <v-card-subtitle class="px-0">
                            <v-icon :color="kingsGold" left>mdi-home</v-icon>Address Information
                        </v-card-subtitle>
                        <v-row>
                            <v-col cols="12" md="8">
                                <v-text-field v-model="applicant.address" label="Home Address" :rules="addressRules"
                                    required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="applicant.postalCode" label="Postal Code"
                                    :rules="postalCodeRules" required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="applicant.country" label="Country" persistent-hint
                                    :rules="countryRules" required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="applicant.city" label="City" :rules="cityRules" required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select v-model="applicant.province" :items="provinceItems" label="Province"
                                    placeholder="Please select..." :rules="provinceRules" required>
                                </v-select>
                            </v-col>
                            <v-col v-if="applicant.province == 'Other'" cols="12">
                                <v-text-field v-model="applicant.provinceOther" label="Province (Other)"
                                    :rules="provinceOtherRules" required>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-card-subtitle class="px-0">
                            <v-icon :color="kingsGold" left>mdi-card-account-details</v-icon>Contact Information
                        </v-card-subtitle>
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="applicant.homePhone" label="Home Phone" :rules="homePhoneRules"
                                    required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="applicant.cellPhone" label="Cell Phone" :rules="cellPhoneRules"
                                    required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="applicant.email" disabled label="Email" :rules="emailRules"
                                    required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field v-model="applicant.emailPersonal" label="Email (Personal)" :rules="personalEmailRules"
                                    required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="6">
                                <v-text-field v-model="applicant.emailPersonalConfirmed" label="Confirm Email (Personal)" :rules="[
                                    v => !!v || 'Please confirm your personal email',
                                    v => v == applicant.emailPersonal || 'Email (personal) entries do not match',
                                ]" required>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-card-subtitle class="px-0">
                            <v-icon :color="kingsGold" left>mdi-school</v-icon>Academic Information
                        </v-card-subtitle>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field v-model="applicant.studentnumber" :counter="9"
                                    :disabled="validStudentnumber" label="Student Number" :rules="studentnumberRules"
                                    required>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-select v-model="applicant.registeredAt" :items="formSettings.registeredAt"
                                    label="Registered at" placeholder="Please select..." required
                                    :rules="registeredAtRules">
                                </v-select>
                            </v-col>
                            <v-col cols="12">
                                <v-select v-model="applicant.yearOfStudy" hint="Note: During the upcoming academic year"
                                    :items="formSettings.yearOfStudy" label="Year of study at King's University College"
                                    persistent-hint placeholder="Please select..." required :rules="yearOfStudyRules">
                                </v-select>
                            </v-col>
                        </v-row>
                        <v-card-subtitle class="px-0">
                            <v-icon :color="kingsGold" left>mdi-calendar-range</v-icon>Residence Term
                        </v-card-subtitle>
                        <v-row>
                            <v-alert v-if="residenceTermsAppliedTo.length > 0" icon="mdi-text-box-check" prominent text
                                type="info" width="100%">
                                You have already applied for the following residence terms:
                                <ul>
                                    <li v-for="(item, i) in residenceTermsAppliedTo" :key="i">{{ item.residenceTerm }}
                                    </li>
                                </ul>
                            </v-alert>
                            <v-col cols="12">
                                <v-select v-model="applicant.residenceTerm" :items="getResidenceTerms"
                                    label="Residence Term" placeholder="Please select..." required
                                    :rules="residenceTermRules">
                                </v-select>
                            </v-col>
                        </v-row>
                        <v-card-subtitle class="px-0">
                            <v-icon :color="kingsGold" left>mdi-information</v-icon>Additional Information
                        </v-card-subtitle>
                        <v-row>
                            <v-col cols="12">
                                <v-select v-model="applicant.internationalStudent" :items="internationalStudentItems"
                                    label="International Student" placeholder="Please select..." required
                                    :rules="internationalStudentRules">
                                </v-select>
                            </v-col>
                            <v-col cols="12" class="pt-4">
                                <v-textarea v-model="applicant.healthDietaryConcern" auto-grow
                                    hint="Specify any health/dietary concern(s) that could be relevant to your stay in residence"
                                    label="Health/Dietary Concern(s)" outlined persistent-hint>
                                </v-textarea>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-card-actions>
                <v-spacer>
                </v-spacer>
                <v-btn class="white--text" :disabled="!valid" :color="successColor" @click="nextStep(2)">
                    Next
                </v-btn>
            </v-card-actions>
        </v-card>
    </div>
</template>

<script>
import Announcement from '../Announcement';

export default {
    name: 'personal-information-form',
    components: {
        Announcement
    },
    props: {
        announcement: {
            required: true
        },
        applicant: {
            required: true
        },
        formSettings: {
            required: true
        },
        photoData: {
            required: true
        },
        residenceTermsAppliedTo: {
            required: true
        },
        validStudentnumber: {
            required: true
        }
    },
    data() {
        return {
            alert: false,
            pronounManualEnter: false,
            addressRules: [
                v => !!v || 'Home address is required',
                v => (v && v.length >= 2) || 'Please provide a valid home address',
            ],
            cellPhoneRules: [
                v => !!v || 'Cell phone is required',
            ],
            cityRules: [
                v => !!v || 'City is required',
                v => (v && v.length >= 2) || 'Please provide a valid city',
            ],
            countryRules: [
                v => !!v || 'Country is required',
                v => (v && v.length >= 2) || 'Please provide a valid country',
            ],
            dateOfBirthMenu: false,
            dateOfBirthRules: [
                v => !!v || 'Date of birth is required',
            ],
            emailRules: [
                v => !!v || 'Email is required',
                v => (v && v.length >= 2) || 'Please provide a valid email',
            ],
            personalEmailRules: [
                v => !!v || 'Email (Personal) is required',
                v => (v && v.length >= 2) || 'Please provide a valid email',
            ],
            firstNameRules: [
                v => !!v || 'First name is required',
                v => (v && v.length >= 2) || 'First name must be more than 2 characters',
            ],
            genderItems: [{
                    text: 'Female',
                    value: 'F'
                },
                {
                    text: 'Male',
                    value: 'M'
                },
                {
                    text: 'Non-Binary',
                    value: 'NonBinary'
                }
            ],
            pronounRules: [
                v => !!v || 'Pronoun is required',
                v => /^([A-Za-z]+)\/([A-Za-z]+)$/.test(v) || 'Pronoun should include forward slash (/). Please refer to above for the format',
            ],
            genderRules: [
                v => !!v || 'Gender is required'
            ],
            homePhoneRules: [
                v => !!v || 'Home phone is required',
            ],
            internationalStudentItems: [{
                    text: 'Yes',
                    value: 'Yes'
                },
                {
                    text: 'No',
                    value: 'No'
                }
            ],
            internationalStudentRules: [
                v => !!v || 'International student is required'
            ],
            lastNameRules: [
                v => !!v || 'Last name is required',
                v => (v && v.length >= 2) || 'Last name must be more than 2 characters',
            ],
            postalCodeRules: [
                v => !!v || 'Postal code is required',
                v => (v && v.length >= 2) || 'Please provide a valid postal code',
            ],
            provinceItems: [{
                    text: 'Alberta',
                    value: 'AB'
                },
                {
                    text: 'British Columbia',
                    value: 'BC'
                },
                {
                    text: 'Manitoba',
                    value: 'MB'
                },
                {
                    text: 'New Brunswick',
                    value: 'NB'
                },
                {
                    text: 'Newfoundland and Labrador',
                    value: 'NL'
                },
                {
                    text: 'Northwest Territories',
                    value: 'NT'
                },
                {
                    text: 'Nova Scotia',
                    value: 'NS'
                },
                {
                    text: 'Nunavut',
                    value: 'NU'
                },
                {
                    text: 'Ontario',
                    value: 'ON'
                },
                {
                    text: 'Prince Edward Island',
                    value: 'PE'
                },
                {
                    text: 'Quebec',
                    value: 'QC'
                },
                {
                    text: 'Saskatchewan',
                    value: 'SK'
                },
                {
                    text: 'Yukon',
                    value: 'YT'
                },
                {
                    text: 'Other',
                    value: 'Other'
                }
            ],
            provinceRules: [
                v => !!v || 'Province is required'
            ],
            provinceOtherRules: [
                v => !!v || 'Province (other) is required',
                v => (v && v.length >= 2) || 'Please provide a valid province (other)',
            ],
            registeredAtRules: [
                v => !!v || 'Registered at is required'
            ],
            residenceTermRules: [
                v => !!v || 'Residence term is required'
            ],
            studentnumberRules: [
                v => !!v || 'Student number is required',
                v => /^\d{9}$/.test(v) || 'Please provide a valid student number (9 digits)',
            ],
            valid: true,
            yearOfStudyRules: [
                v => !!v || 'Year of study is required'
            ],
            sixftoroverdetailitems: [
                {text: "Please Select...", value: ""},
                {text: "6' 0”", value: "6.0"},
                {text: "6' 1”", value: "6.1"},
                {text: "6' 2”", value: "6.2"},
                {text: "6' 3”", value: "6.3"},
                {text: "6' 4”", value: "6.4"},
                {text: "6' 5”", value: "6.5"}
            ],
            showheightoptions: false
        }
    },
    methods: {
        nextStep(val) {
            if (this.$refs.personalInformationForm.validate()) {
                this.alert = false;
                this.$emit('setStep', val);
                this.$nextTick(() => {
                    this.$emit('scrollToTop');
                })
            } else {
                this.alert = true;
                this.$emit('scrollToTop');
            }
        },
        resetForm() {
            this.$refs.personalInformationForm.resetValidation()
        },
        saveDateOfBirth() {
            this.$refs.menu.save(this.applicant.dateOfBirth)
        },
        enterPronounManually(){
            const that = this;
            if(that.applicant.prefpronoun == 'He/Him' || that.applicant.prefpronoun == 'She/Her'){
                that.pronounManualEnter = false;
            }else {
                that.pronounManualEnter = true;
            }
        },
        showHeightOptions(){
            const that = this;
            
            if(that.applicant.sixftorover == 'yes'){
                that.showheightoptions = true;
            }else {
                that.applicant.sixftoroverdetail = '';
                
                that.showheightoptions = false;
            }
        }
    },
    computed: {
        dangerColor() {
            return '#f64e60';
        },
        getResidenceTerms() {
            let residenceTerms = [];

            if (this.formSettings.residenceTerms && this.residenceTermsAppliedTo) {
                for (let i = 0; i < this.formSettings.residenceTerms.length; i++) {
                    let bool = true;
                    let term = this.formSettings.residenceTerms[i].value;

                    for (let j = 0; j < this.residenceTermsAppliedTo.length; j++) {
                        if (term == this.residenceTermsAppliedTo[j].residenceTerm) {
                            bool = false;
                        }
                    }

                    if (bool) {
                        residenceTerms.push(this.formSettings.residenceTerms[i])
                    }
                }
            }

            return residenceTerms;
        },
        kingsGold() {
            return '#d7ae28';
        },
        successColor() {
            return '#1bc5bd';
        }
    }
}
</script>

<style scoped>
.col-12 {
    padding-top: 0;
}
</style>